import { ArrowRightLeft } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import type { JobCancellationReason } from '@/types'

type WorkflowStatusNoteDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetLabel: string
  targetCode?: string | null
  isReason: boolean
  note: string
  onNoteChange: (note: string) => void
  onConfirm: () => void
  isPending?: boolean
  contextDescription?: string
  entityLabel?: string
  cancellationReasons?: JobCancellationReason[]
}

export function WorkflowStatusNoteDialog({
  open,
  onOpenChange,
  targetLabel,
  targetCode,
  isReason,
  note,
  onNoteChange,
  onConfirm,
  isPending = false,
  contextDescription,
  entityLabel = 'Record',
  cancellationReasons = [],
}: WorkflowStatusNoteDialogProps) {
  const trimmedNote = note.trim()
  const confirmDisabled = isPending || (isReason && !trimmedNote)
  const quickReasons = cancellationReasons.filter((reason) => reason.label?.trim())

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isPending && onOpenChange(nextOpen)}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="border-b bg-muted/30 px-6 py-5">
          <DialogHeader className="space-y-3 text-left">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <ArrowRightLeft className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-xl">
                {isReason ? 'Add a reason' : 'Add a note'}
              </DialogTitle>
              <DialogDescription>
                {isReason
                  ? `Tell us why this ${entityLabel.toLowerCase()} status is changing.`
                  : `Optional context helps the team understand this ${entityLabel.toLowerCase()} update.`}
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="rounded-2xl border border-border/70 bg-background p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Changing to
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="rounded-full px-3 py-1 text-sm font-medium">
                {targetLabel}
              </Badge>
              {targetCode && targetCode !== targetLabel ? (
                <span className="text-xs text-muted-foreground">{targetCode}</span>
              ) : null}
            </div>
            {contextDescription ? (
              <p className="mt-3 text-sm text-muted-foreground">{contextDescription}</p>
            ) : null}
          </div>

          {isReason && quickReasons.length > 0 ? (
            <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-4">
              <p className="text-sm font-medium text-amber-950">Quick reasons</p>
              <p className="mt-1 text-xs text-amber-900/80">Pick one or write your own below.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {quickReasons.map((reason) => (
                  <button
                    key={String(reason.id ?? reason.code ?? reason.label)}
                    type="button"
                    className="rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-medium text-amber-950 transition-colors hover:border-amber-300 hover:bg-amber-100"
                    onClick={() => onNoteChange(String(reason.label ?? ''))}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="workflow-status-note">
              {isReason ? 'Reason' : 'Note'}
              {isReason ? ' *' : ''}
            </Label>
            <Textarea
              id="workflow-status-note"
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
              placeholder={
                isReason
                  ? 'Why is this status changing?'
                  : 'Add any helpful details for this status change'
              }
              rows={4}
              autoFocus
              disabled={isPending}
              className="min-h-28 resize-none rounded-xl"
            />
            <p className="text-xs text-muted-foreground">
              {isReason
                ? 'A reason is required before this status change can be saved.'
                : 'This note will be stored with the status change history.'}
            </p>
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-4 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-xl"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Confirm status change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
